import { VStack } from "@chakra-ui/react"
import { useState, useMemo, useCallback } from "react"
import { getAlertSeverityColor } from "@hydrologiq/shared/components/AlertSeverityBadge"
import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import { KanbanBoard, KANBAN_BOARD_GAPS } from "./index"
import type { KanbanViewMode } from "./index"
import { useUpdateAlertStatus } from "@hydrologiq/shared/hooks/useAlertMutations"
import { useUpdateAlert } from "@hydrologiq/shared/hooks/useAlertUpdate"
import type { AssetConfig } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"

export interface AlertsKanbanData {
  alerts: {
    new: any[]
    in_progress: any[]
    on_hold: any[]
    completed: any[]
  }
  headlines: {
    new_issues?: { count: number; critical: number; warning: number; info: number }
    in_progress_issues?: {
      count: number
      critical: number
      warning: number
      info: number
      acknowledged: number
      attended: number
    }
    on_hold_issues?: { count: number; critical: number; warning: number; info: number }
    customers_affected?: number
    systems_affected?: number
    resolution_time_hours?: number | null
    resolution_time_hours_7_14d?: number | null
  }
  assignable_users: any[]
  system_order_ids?: string[]
  pinned_system_ids?: string[]
}

export interface AlertsKanbanViewProps {
  data: AlertsKanbanData | undefined
  loading: boolean
  error?: any
  /** Refetch queries to pass to mutations (e.g. ["GetSystemAlerts"]) */
  refetchQueries?: string[]
  /** System ID context for mutations refetch */
  systemId?: string
  /** Whether to hide the customer/site/system grid on cards */
  systemView?: boolean
  /** Highlighted alert ID (e.g. from URL params) */
  highlightedAlertId?: string
  /** Highlighted asset ID */
  highlightedAssetId?: string
  /** Callback when highlight is dismissed */
  onHighlightDismiss?: () => void
  /** Asset configurations for consistent graph colors in data popovers */
  assetConfigs?: AssetConfig[]
  /** Hide asset link (when viewing within an asset context) */
  assetView?: boolean
}

function formatHours(h: number): string {
  return `${Math.floor(h)}h ${Math.floor((h % 1) * 60)}m`
}

export function AlertsKanbanView({
  data,
  loading,
  error,
  systemId,
  systemView = false,
  highlightedAlertId,
  highlightedAssetId,
  onHighlightDismiss,
  assetConfigs,
  assetView = false,
}: AlertsKanbanViewProps) {
  const [kanbanViewMode, setKanbanViewMode] = useState<KanbanViewMode>("ungrouped")

  const { updateStatus, loading: statusLoading } = useUpdateAlertStatus(undefined, systemId)
  const { update: updateAlert, loading: updateLoading } = useUpdateAlert(undefined, systemId)

  const alerts = useMemo(() => data?.alerts ?? {
    new: [],
    in_progress: [],
    on_hold: [],
    completed: [],
  }, [data?.alerts])

  const assignableUsers = useMemo(() => data?.assignable_users ?? [], [data?.assignable_users])
  const systemOrderIds = useMemo(() => data?.system_order_ids ?? [], [data?.system_order_ids])
  const pinnedSystemIds = useMemo(() => data?.pinned_system_ids ?? [], [data?.pinned_system_ids])

  const headlines = data?.headlines ?? {}

  const handleStatusChange = useCallback(async (
    alertId: string,
    newStatus: "new" | "acknowledged" | "attended" | "on_hold" | "completed",
    note?: string,
  ) => {
    await updateStatus(alertId, newStatus, note)
  }, [updateStatus])

  const handleAttended = useCallback(async (alertId: string) => {
    await updateStatus(alertId, "attended", "Attended")
  }, [updateStatus])

  const newIssues = headlines.new_issues
  const inProgressIssues = headlines.in_progress_issues
  const onHoldIssues = headlines.on_hold_issues
  const resolutionTime = headlines.resolution_time_hours
  const resolutionTimePrior = headlines.resolution_time_hours_7_14d

  const resolutionTimeStr = resolutionTime != null ? formatHours(resolutionTime) : "—"
  const resolutionTimePriorStr = resolutionTimePrior != null ? formatHours(resolutionTimePrior) : null

  const criticalColor = getAlertSeverityColor("critical")
  const warningColor = getAlertSeverityColor("warning")
  const okColor = "green"

  if (error) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Figure heading="Error">
          <FigureContent.Root>
            <FigureContent.Value color="red">
              Failed to load alerts
            </FigureContent.Value>
          </FigureContent.Root>
        </Figure>
      </VStack>
    )
  }

  return (
    <VStack align="stretch" w="100%" gap={KANBAN_BOARD_GAPS}>
      <FigureGrid loading={loading}>
        <Figure heading="New Issues">
          <FigureContent.Root>
            <FigureContent.Value color={newIssues?.critical ? criticalColor : okColor}>
              {newIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {newIssues && (
              <FigureContent.Label color={newIssues?.warning ? warningColor : okColor} fontWeight="semibold">
                {newIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="In Progress">
          <FigureContent.Root>
            <FigureContent.Value color={inProgressIssues?.critical ? criticalColor : okColor}>
              {inProgressIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {inProgressIssues && (
              <FigureContent.Label color={inProgressIssues?.warning ? warningColor : okColor} fontWeight="semibold">
                {inProgressIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="On Hold">
          <FigureContent.Root>
            <FigureContent.Value color={onHoldIssues?.critical ? criticalColor : okColor}>
              {onHoldIssues?.critical ?? 0} Critical
            </FigureContent.Value>
            {onHoldIssues && (
              <FigureContent.Label color={onHoldIssues?.warning ? warningColor : okColor} fontWeight="semibold">
                {onHoldIssues.warning ?? 0} Warning
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
        <Figure heading="Resolution Time">
          <FigureContent.Root>
            <FigureContent.Value>{resolutionTimeStr}</FigureContent.Value>
            {resolutionTimePriorStr && (
              <FigureContent.Label fontWeight="semibold">
                vs {resolutionTimePriorStr} (7-14d ago)
              </FigureContent.Label>
            )}
          </FigureContent.Root>
        </Figure>
      </FigureGrid>

      {!loading && (
        <KanbanBoard
          alerts={alerts}
          onStatusChange={handleStatusChange}
          onAttended={handleAttended}
          onUpdate={updateAlert}
          isUpdating={statusLoading || updateLoading}
          enableSearch={true}
          isPreview={false}
          assignableUsers={assignableUsers}
          viewMode={kanbanViewMode}
          onViewModeChange={setKanbanViewMode}
          systemOrderIds={systemOrderIds}
          pinnedSystemIds={pinnedSystemIds}
          highlightedAlertId={highlightedAlertId}
          highlightedAssetId={highlightedAssetId}
          onHighlightDismiss={onHighlightDismiss}
          hideViewModeToggle={true}
          systemView={systemView}
          assetConfigs={assetConfigs}
          assetView={assetView}
        />
      )}
    </VStack>
  )
}
